/**
 * The map controls: upload an image, choose which one is in play, drop
 * waypoints, and pick the view. Every write here is gated on the server —
 * `host:set_world_map` and `host:set_encounter_map` both require the
 * campaign's **Owner**, and `POST /places` runs its own check.
 *
 * The plan puts most of this in the setup wizard, and it belongs there — but a
 * map that can only be annotated before the first session is a map nobody
 * finishes annotating. Reaching it from the live Map tab removes the reason
 * to keep a second flow in sync.
 *
 * **Shown only to the Owner — `CampaignPage` gates the render on `amOwner`,
 * this component does not check the viewer's role itself.** `e2e/fog.spec.ts`
 * once defended showing this to every player, on the reasoning that fog on
 * the *data* (`visiblePlaces`) is the real boundary and hiding the
 * *control* is a UI nicety, not security. That is still true — the gate
 * here is not what stops a player from seeing an unrevealed place — but it
 * answers a security question, not the UX one: every player being shown a
 * functional-looking upload/scale/reveal form that always no-ops for them
 * is exactly the "switch that silently does nothing" `SettingsPanel`'s own
 * docstring warns against. The gate is real now; do not remove it on the
 * grounds that fog already makes it safe without one — that argument was
 * true before too and answers a different question.
 *
 * **Viewing the map is a separate surface** (`WorldMap`, rendered directly
 * by the Map tab, independent of `mapMode`, for every player) — this
 * component is only the editing form.
 */

import { titleLabel } from '../labels.js';
import { useCallback, useEffect, useState } from 'react';

import type { AssetKind } from '@portal/shared';

import { api, ApiError } from '../api.js';
import { RegionEditor } from './RegionEditor.js';

export interface AssetRow {
  readonly id: string;
  readonly key: string;
  readonly kind: AssetKind;
  readonly url?: string;
}

export interface MapAdminPanelProps {
  readonly campaignId: string;
  readonly inCombat: boolean;
  /**
   * What the map is missing, for a table that predates the setup gate.
   *
   * New campaigns cannot open until both exist, so this is only ever true of an
   * older table — and it **warns rather than locks**, because shutting an
   * in-progress group out of their own game to enforce a rule added afterwards
   * would be the cure being worse.
   */
  readonly worldMapUrl?: string | undefined;
  /**
   * Coordinates the Owner clicked on the map, if they have clicked one.
   *
   * The panel holds the form; the *image* is where you point at a spot. Typing
   * `mapX: 43.2` is technically the same act and nobody does it twice.
   */
  readonly picked?: { readonly mapX: number; readonly mapY: number } | undefined;
  readonly onPickedUsed?: () => void;
  readonly onCommand: (event: string, payload?: Record<string, unknown>) => void;
  /** Fired after a place is created, so the map picks up the new waypoint. */
  readonly onPlacesChanged: () => void;
  /** The regions already revealed, so the Reveal control can show which are done. */
  readonly revealed?: readonly string[];
  /** Region ids the placed pins sit in, which may never have been named as regions. */
  readonly pinRegions?: readonly string[];
  /**
   * Fired after a world is imported, which changes more than the places do.
   *
   * Distinct from `onPlacesChanged` deliberately: an import rewrites the terrain
   * grid too, and that is a separate request from `/places` precisely so that
   * adding one waypoint does not re-fetch the largest column in the schema.
   *
   * **Required, not optional with a fallback.** Defaulting it to
   * `onPlacesChanged` would let a second call site silently reload the lines and
   * not the ground — the half-drawn import this exists to prevent, arriving by
   * omission. This repo has been caught three times by a new path lacking a
   * guard the old one inherited from a callee.
   */
  /**
   * The active fight's own scale, already divided by 100 — absent means
   * positioning was never opted into. Read from `combat`, not fetched here:
   * unlike `worldMapScaleMiles`, this lives on the ephemeral combat blob and
   * has no HTTP route of its own, so the page (which already holds `combat`
   * for the map surface) passes it straight through.
   */
  readonly encounterMapScaleFeetPerPercent?: number | undefined;
}

/**
 * The two kinds this surface deals in. Portraits belong to the sheet.
 *
 * Typed as `AssetKind` rather than free strings, so a rename in the contract
 * fails to compile here instead of producing an upload the server rejects.
 */
const MAP_KINDS: readonly AssetKind[] = ['world_map', 'encounter_map'];

export interface ExistingPlace {
  readonly id: string;
  readonly name: string;
  readonly waypoint?: { readonly mapX: number; readonly mapY: number; readonly regionId: string };
}

/**
 * Which write the place form is: a new place, or a pin on one that exists.
 *
 * **Pure and exported, because the two branches are a different request** — a
 * POST that creates and a PATCH that pins — and sending the wrong one is a
 * refusal the Owner reads as the button being broken. `renderToStaticMarkup`
 * cannot press a button, so this is where it can be asserted; the same shape as
 * `checkPayload` and `pointFromClick`.
 *
 * The pin branch exists because **the DM writes places and cannot pin them**:
 * the directive block carries a name and nothing else, so the entire generated
 * world sat off the map with no control that could put it on. A new group's
 * first journey needs coordinates at both ends, and the party stands where
 * world generation put them.
 *
 * A pin needs all three coordinates; creating needs a name and allows none.
 * Neither is guessed — `undefined` means the form does not yet say what it is.
 */
export function placeWriteFor(input: {
  readonly campaignId: string;
  readonly existingId: string;
  readonly name: string;
  readonly mapX: string;
  readonly mapY: string;
  readonly regionId: string;
}):
  | { method: 'post' | 'patch'; path: string; body: Record<string, unknown>; label: string }
  | undefined {
  const mapX = input.mapX.trim();
  const mapY = input.mapY.trim();
  const regionId = input.regionId.trim();
  const coordinates =
    mapX === '' && mapY === '' && regionId === ''
      ? undefined
      : { mapX: Number(mapX), mapY: Number(mapY), regionId };

  if (input.existingId !== '') {
    // Pinning is the whole point of the branch, so it refuses to send an empty
    // body that the server would answer 400 to — and never sends `waypoint:
    // null`, which is *unpinning* and is a different button.
    if (!coordinates) return undefined;
    return {
      method: 'patch',
      path: `/api/campaigns/${input.campaignId}/places/${input.existingId}`,
      body: coordinates,
      label: 'it',
    };
  }

  const name = input.name.trim();
  if (name === '') return undefined;
  return {
    method: 'post',
    path: `/api/campaigns/${input.campaignId}/places`,
    body: { name, ...(coordinates ?? {}) },
    label: name,
  };
}

/**
 * What "Set" on the encounter scale sends, or nothing at all.
 *
 * Pure and exported for the same reason `placeWriteFor` is — a button whose
 * click handler silently no-ops looks identical to a working one in every
 * other assertion. Refuses non-positive and unparseable input rather than
 * sending it: `host:set_encounter_map` would refuse it too, but a round
 * trip to the server is a worse way to catch "the box was empty" than
 * never emitting at all.
 */
export function encounterScalePayload(input: string): { scaleFeet: number } | undefined {
  const feet = Number(input);
  return Number.isFinite(feet) && feet > 0 ? { scaleFeet: feet } : undefined;
}

const MAP_MODES = ['theatre', 'encounterGrid', 'combatGrid', 'worldMap'] as const;

/** What each view is called to the person choosing it; the keys above are what the server stores. */
const MAP_MODE_LABELS: Record<(typeof MAP_MODES)[number], string> = {
  theatre: 'Scene (no map)',
  encounterGrid: 'Encounter grid',
  combatGrid: 'Combat grid',
  worldMap: 'World map',
};

/**
 * The regions the Reveal control lists: every one the map has (named, or with a pin in it), each marked revealed
 * or not. A region that was revealed but is no longer on the map (a typo from before the server checked) is
 * listed too, flagged, so it is not a silent entry nobody can see.
 */
export function revealChoices(
  named: readonly { readonly regionId: string; readonly name: string }[],
  pinRegions: readonly string[],
  revealed: readonly string[],
): readonly { id: string; label: string; revealed: boolean; onMap: boolean }[] {
  const names = new Map(named.map((region) => [region.regionId, region.name]));
  const onMap = new Set([...names.keys(), ...pinRegions]);
  const all = new Set([...onMap, ...revealed]);
  return [...all]
    .sort((a, b) => (names.get(a) ?? a).localeCompare(names.get(b) ?? b))
    .map((id) => ({
      id,
      label: names.get(id) ?? titleLabel(id),
      revealed: revealed.includes(id),
      onMap: onMap.has(id),
    }));
}

export function MapAdminPanel({
  campaignId,
  inCombat,
  worldMapUrl,
  picked,
  onPickedUsed,
  onCommand,
  onPlacesChanged,
  revealed = [],
  pinRegions = [],
  encounterMapScaleFeetPerPercent,
}: MapAdminPanelProps) {
  const [assets, setAssets] = useState<AssetRow[]>([]);
  const [kind, setKind] = useState<AssetKind>('world_map');
  const [namedRegions, setNamedRegions] = useState<readonly { regionId: string; name: string }[]>([]);
  const [status, setStatus] = useState<string | null>(null);
  // The regions the map actually has, for the Reveal control: it offers these, not a box to type a guess into.
  useEffect(() => {
    void api
      .get<{ regions: readonly { regionId: string; name: string }[] }>(`/api/campaigns/${campaignId}/regions`)
      .then((body) => setNamedRegions(body.regions ?? []))
      .catch(() => setNamedRegions([]));
  }, [campaignId, revealed.length]);
  // See the world-import `details` below: closed, it still occupied 2,620px.
  const [importOpen, setImportOpen] = useState(false);
  /**
   * Whether this campaign has a world at all, and a way to give it one.
   *
   * ⚠ **The generate button used to exist on exactly one screen with no URL** —
   * the post-creation state of `NewCampaignPage`. Leave that screen and it was
   * gone for good, so a campaign could be, and was, played for 44 beats with
   * zero canon places. Everything downstream then degrades quietly: with no
   * places `movementInstruction` returns `''`, the DM is never offered the
   * location line, `partyLocation` is never set, and the model invents the
   * setting from nothing and reinvents it every beat.
   *
   * This is the reachable second door. It is deliberately a plain warning and a
   * button rather than a redesign — the real fix is the setup waterfall in
   * `docs/playtest-2026-08-25.md` (#1, #4, #10, #11, #15).
   */
  const [seeded, setSeeded] = useState<boolean | null>(null);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    let live = true;
    void api
      .get<{ campaign: { worldState?: { worldSeeded?: boolean } } }>(
        `/api/campaigns/${campaignId}`,
      )
      .then((body) => {
        if (live) setSeeded(body.campaign.worldState?.worldSeeded === true);
      })
      .catch(() => {
        // Unknown is not "missing": a failed read must not accuse a table that
        // has a perfectly good world of having none.
        if (live) setSeeded(null);
      });
    return () => {
      live = false;
    };
  }, [campaignId]);

  const generateWorld = () => {
    setSeeding(true);
    setStatus(null);
    void api
      .post<{ created: number }>(`/api/campaigns/${campaignId}/canon/generate`, {})
      .then((body) => {
        setSeeded(true);
        setStatus(`the world is written — ${body.created} places, people and factions`);
        onPlacesChanged?.();
      })
      .catch((e: unknown) =>
        setStatus(e instanceof ApiError ? e.message : 'the world could not be generated'),
      )
      .finally(() => setSeeding(false));
  };

  // The typed value, in feet across the FULL image width — what the Owner
  // actually measures — never the already-divided-by-100 figure the wire
  // carries. Starts blank rather than pre-filled from the current scale: a
  // number in the box before anybody touched it reads as a decision this
  // form made, not one the Owner did.
  const [encounterScaleInput, setEncounterScaleInput] = useState('');
  const [placeName, setPlaceName] = useState('');
  const [placeRegion, setPlaceRegion] = useState('');
  const [placeX, setPlaceX] = useState('');
  const [scale, setScale] = useState('');
  const [placeY, setPlaceY] = useState('');
  /** '' means "make a new place"; an id means "put that one on the map". */
  const [pinTarget, setPinTarget] = useState('');
  const [places, setPlaces] = useState<ExistingPlace[]>([]);

  // **Read, not passed.** The scale lives in world state and the map surface
  // does not carry it, so the panel asks — which also lets it *show* the
  // current scale instead of an empty box the Owner has to guess at.
  const [scaleMiles, setScaleMiles] = useState<number | undefined>(undefined);
  const loadScale = useCallback(() => {
    void api
      .get<{ campaign: { worldState?: { worldMapScaleMiles?: number } } }>(
        `/api/campaigns/${campaignId}`,
      )
      .then((body) => setScaleMiles(body.campaign?.worldState?.worldMapScaleMiles))
      .catch(() => undefined);
  }, [campaignId]);

  useEffect(loadScale, [loadScale]);

  const loadPlaces = useCallback(() => {
    void api
      .get<{ places: ExistingPlace[] }>(`/api/campaigns/${campaignId}/places`)
      .then((body) => setPlaces(body.places ?? []))
      .catch(() => undefined);
  }, [campaignId]);

  useEffect(loadPlaces, [loadPlaces]);

  const loadAssets = useCallback(() => {
    void api
      .get<{ assets: AssetRow[] }>(`/api/campaigns/${campaignId}/assets`)
      .then((body) => setAssets(body.assets ?? []))
      .catch(() => undefined);
  }, [campaignId]);

  useEffect(loadAssets, [loadAssets]);

  /**
   * The upload posts **raw bytes**, not multipart.
   *
   * The endpoint is `express.raw` over an allowlist of image content types, so
   * the file's own type is the whole of the negotiation — which is also what
   * makes the server's type check the same check the browser sent.
   */
  const upload = (file: File) => {
    setStatus(`uploading ${file.name}…`);
    void fetch(`/api/campaigns/${campaignId}/assets?kind=${kind}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': file.type },
      body: file,
    })
      .then(async (res) => {
        if (!res.ok) {
          const body: unknown = await res.json().catch(() => undefined);
          const message =
            typeof body === 'object' && body !== null && 'error' in body
              ? String((body as { error: unknown }).error)
              : `upload failed (${res.status})`;
          throw new ApiError(res.status, message);
        }
        setStatus(`uploaded ${file.name}`);
        loadAssets();
      })
      .catch((error: unknown) => {
        setStatus(error instanceof ApiError ? error.message : 'upload failed');
      });
  };

  // **Filled from the click, not locked to it.** A pin dropped a little off
  // is corrected by typing, and an Owner who clicks and then changes their mind
  // should not have to click again to nudge it.
  useEffect(() => {
    if (!picked) return;
    setPlaceX(picked.mapX.toFixed(1));
    setPlaceY(picked.mapY.toFixed(1));
    onPickedUsed?.();
  }, [picked, onPickedUsed]);

  const createPlace = () => {
    const write = placeWriteFor({
      campaignId,
      existingId: pinTarget,
      name: placeName,
      mapX: placeX,
      mapY: placeY,
      regionId: placeRegion,
    });
    if (!write) {
      setStatus('name a new place, or choose one already on the list to pin');
      return;
    }

    const done = write.method === 'patch' ? 'pinned' : 'added';
    void (write.method === 'patch'
      ? api.patch(write.path, write.body)
      : api.post(write.path, write.body)
    )
      .then(() => {
        setStatus(`${done} ${write.label}`);
        setPlaceName('');
        setPlaceX('');
        setPlaceY('');
        setPinTarget('');
        loadPlaces();
        onPlacesChanged();
      })
      .catch((error: unknown) => {
        setStatus(error instanceof ApiError ? error.message : `could not ${done} that place`);
      });
  };

  const saveScale = () => {
    void api
      .put(`/api/campaigns/${campaignId}/map-scale`, { scaleMiles: scale.trim() })
      .then(() => {
        loadScale();
        setStatus(
          scale.trim() === '' ? 'scale cleared' : `map is ${scale.trim()} miles across`,
        );
      })
      .catch((error: unknown) => {
        setStatus(error instanceof ApiError ? error.message : 'that scale was refused');
      });
  };

  const worldMaps = assets.filter((asset) => asset.kind === 'world_map');
  const encounterMaps = assets.filter((asset) => asset.kind === 'encounter_map');

  return (
    <div className="space-y-3 text-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
        Map administration
      </p>
        {/* ⚠ **First, because nothing else in here matters without it.** A
            campaign with no canon has no places for the DM to put anybody in,
            and the failure is silent: the prose stays fluent and invents a
            setting from nothing, a different one every beat. Loud, and offering
            the fix in place, because the only other door to it is a screen with
            no URL that you cannot navigate back to. */}
        {seeded === false && (
          <div className="space-y-2 rounded-md border border-amber-500/60 bg-amber-500/10 p-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
              This table has no world
            </p>
            <p className="text-xs text-neutral-700 dark:text-neutral-300">
              Nothing has been written from your premise, so there are no places, people or
              factions. The DM will invent a setting each beat and contradict itself. Generate it
              before anyone plays — it runs <strong>once</strong>.
            </p>
            <button
              type="button"
              disabled={seeding}
              onClick={generateWorld}
              className="rounded-md border border-amber-500/60 px-3 py-1 text-xs font-medium disabled:opacity-50"
            >
              {seeding ? 'Writing the world…' : 'Generate the world'}
            </button>
          </div>
        )}

        {/* **Fog reveal is the Owner's.** What a table has seen of its own
            map is administration, on the same footing as which image the map is
            drawn from — which is the next control down. The server gates it on
            the Owner.

            **Fog only ever uncovers.** `world_state.revealed` is the list of
            what has been *seen*, so a region added to the map later is hidden
            by default and nothing here can put a region back. That is the way
            round a mistake should fail. */}
        <div className="space-y-1">
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Reveal a map region. Players see the places in a region only once it is revealed, and arriving somewhere
            reveals its region by itself; this is for showing them somewhere they have not been.
          </p>
          {(() => {
            const choices = revealChoices(namedRegions, pinRegions, revealed);
            if (choices.length === 0) {
              return (
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  This map has no regions yet, so there is nothing to reveal. Name regions below, or put a place on the
                  map and give it a region.
                </p>
              );
            }
            return (
              <ul className="space-y-1" aria-label="Map regions">
                {choices.map((choice) => (
                  <li key={choice.id} className="flex items-center gap-2 text-xs">
                    <span className="flex-1">
                      {choice.label}
                      {!choice.onMap && (
                        <span className="ml-1 text-neutral-500 dark:text-neutral-400">(not on this map)</span>
                      )}
                    </span>
                    {choice.revealed ? (
                      <span className="text-emerald-700 dark:text-emerald-400">revealed</span>
                    ) : (
                      <button
                        type="button"
                        aria-label={`Reveal ${choice.label}`}
                        onClick={() => onCommand('host:reveal', { regionIds: [choice.id] })}
                        className="rounded-md border border-neutral-300 px-2 py-0.5 dark:border-neutral-700"
                      >
                        Reveal
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            );
          })()}
        </div>

        <div className="space-y-1">
          <p className="text-xs text-neutral-500 dark:text-neutral-400">The view everyone is looking at</p>
          <div className="flex flex-wrap gap-1">
            {MAP_MODES.map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => onCommand('host:set_mode', { mapMode: mode })}
                className="rounded-md border border-neutral-300 px-2 py-0.5 text-xs dark:border-neutral-700"
              >
                {MAP_MODE_LABELS[mode]}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-1">
          <label className="block text-xs text-neutral-500 dark:text-neutral-400" htmlFor="asset-kind">
            Upload an image
          </label>
          <div className="flex flex-wrap gap-2">
            <select
              id="asset-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as AssetKind)}
              className="rounded-md border border-neutral-300 bg-white px-1 py-0.5 text-xs dark:border-neutral-700 dark:bg-neutral-900"
            >
              {MAP_KINDS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <input
              type="file"
              aria-label="Map image to upload"
              // The same allowlist the server enforces. No SVG: it is a
              // document format that executes script, and serving one from our
              // own origin would be stored XSS in the shape of a map.
              accept="image/png,image/jpeg,image/webp,image/gif"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) upload(file);
                // Clears the input so re-picking the same file fires again.
                e.target.value = '';
              }}
              className="text-xs file:mr-2 file:rounded-md file:border file:border-neutral-300 file:bg-white file:px-2 file:py-0.5 file:text-xs file:font-medium file:text-neutral-900 hover:file:bg-neutral-50 dark:file:border-neutral-700 dark:file:bg-neutral-900 dark:file:text-neutral-100 dark:hover:file:bg-neutral-800"
            />
          </div>
        </div>

        <div className="space-y-1">
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {/* Uploading a candidate and choosing the one in play are
                different decisions, so they are different controls. */}
            World map in play
          </p>
          <div className="flex flex-wrap gap-1">
            {worldMaps.map((asset) => (
              <button
                key={asset.id}
                type="button"
                onClick={() => onCommand('host:set_world_map', { mapKey: asset.key })}
                className="max-w-40 truncate rounded-md border border-neutral-300 px-2 py-0.5 text-xs dark:border-neutral-700"
              >
                {shortKey(asset.key)}
              </button>
            ))}
            {worldMaps.length > 0 && (
              <button
                type="button"
                // Absent clears it, which is a decision an Owner can want.
                onClick={() => onCommand('host:set_world_map')}
                className="rounded-md border border-neutral-300 px-2 py-0.5 text-xs dark:border-neutral-700"
              >
                none
              </button>
            )}
            {worldMaps.length === 0 && (
              <span className="text-xs text-neutral-500 dark:text-neutral-400">nothing uploaded yet</span>
            )}
          </div>
        </div>

        {inCombat && (
          <div className="space-y-1">
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              {/* Belongs to this fight: ending combat clears both. */}
              This encounter
            </p>
            <div className="flex flex-wrap gap-1">
              {encounterMaps.map((asset) => (
                <button
                  key={asset.id}
                  type="button"
                  onClick={() => onCommand('host:set_encounter_map', { mapKey: asset.key })}
                  className="max-w-40 truncate rounded-md border border-neutral-300 px-2 py-0.5 text-xs dark:border-neutral-700"
                >
                  {shortKey(asset.key)}
                </button>
              ))}
              {encounterMaps.length === 0 && (
                <span className="text-xs text-neutral-500 dark:text-neutral-400">no encounter maps uploaded</span>
              )}
            </div>

            {/* **Opt-in, per encounter.** Absent, a fight runs on zones
                exactly as it always has — a scale is what turns tokens and a
                `move` action on at all, so this is the one control that
                switches positioning on. Feet across the FULL image width,
                the same thing the Owner would actually measure with a ruler
                on a printed battlemat; the server divides by 100 to get feet per
                percentage point. */}
            <div className="flex flex-wrap items-center gap-1 pt-1">
              <label className="text-xs text-neutral-500 dark:text-neutral-400" htmlFor="encounter-scale-feet">
                Map scale
              </label>
              <input
                id="encounter-scale-feet"
                type="number"
                min="1"
                step="1"
                placeholder="feet across the image"
                value={encounterScaleInput}
                onChange={(e) => setEncounterScaleInput(e.target.value)}
                className="w-36 rounded-md border border-neutral-300 px-2 py-0.5 text-xs dark:border-neutral-700 dark:bg-neutral-900"
              />
              <button
                type="button"
                aria-label="Set encounter map scale"
                disabled={!encounterScaleInput}
                onClick={() => {
                  const payload = encounterScalePayload(encounterScaleInput);
                  if (payload) onCommand('host:set_encounter_map', payload);
                }}
                className="rounded-md border border-neutral-300 px-2 py-0.5 text-xs disabled:opacity-40 dark:border-neutral-700"
              >
                Set
              </button>
              {encounterMapScaleFeetPerPercent !== undefined && (
                <>
                  <span className="text-xs text-neutral-500 dark:text-neutral-400">
                    currently {Math.round(encounterMapScaleFeetPerPercent * 100)} ft across
                  </span>
                  <button
                    type="button"
                    aria-label="Clear encounter map scale"
                    onClick={() => onCommand('host:set_encounter_map', { scaleFeet: null })}
                    className="rounded-md border border-neutral-300 px-2 py-0.5 text-xs dark:border-neutral-700"
                  >
                    Clear
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        <div className="space-y-1">
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {/* Percentages, so a pin survives the image being replaced. */}
            Add a place — coordinates are percentages of the map image
          </p>
          {/* **The DM's places, which nothing could put on the map.** A place
              the model wrote carries a name and a description and no
              coordinates, and until this there was no control that could add
              them — so the whole generated world stayed off the map and the
              party, standing at one of those places, could never take a first
              journey. Unpinned ones first, because they are the reason this
              exists. */}
          <select
            aria-label="Pin a place already in the world bible"
            value={pinTarget}
            onChange={(e) => setPinTarget(e.target.value)}
            className="w-full rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
          >
            <option value="">a new place…</option>
            {[...places]
              .sort((a, b) => Number(Boolean(a.waypoint)) - Number(Boolean(b.waypoint)))
              .map((place) => (
                <option key={place.id} value={place.id}>
                  {place.waypoint ? `${place.name} (move its pin)` : `${place.name} — not on the map`}
                </option>
              ))}
          </select>
          <input
            value={placeName}
            onChange={(e) => setPlaceName(e.target.value)}
            aria-label="Place name"
            placeholder="Ashford Ruins"
            disabled={pinTarget !== ''}
            className="w-full rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs disabled:opacity-40 dark:border-neutral-700 dark:bg-neutral-900"
          />
          <div className="flex flex-wrap gap-1">
            <input
              value={placeX}
              onChange={(e) => setPlaceX(e.target.value)}
              aria-label="Place x, as a percentage"
              inputMode="decimal"
              placeholder="x%"
              className="w-16 rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
            />
            <input
              value={placeY}
              onChange={(e) => setPlaceY(e.target.value)}
              aria-label="Place y, as a percentage"
              inputMode="decimal"
              placeholder="y%"
              className="w-16 rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
            />
            <input
              value={placeRegion}
              onChange={(e) => setPlaceRegion(e.target.value)}
              aria-label="Fog region this place sits in"
              placeholder="region"
              className="w-24 rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
            />
            <button
              type="button"
              disabled={
                placeWriteFor({
                  campaignId,
                  existingId: pinTarget,
                  name: placeName,
                  mapX: placeX,
                  mapY: placeY,
                  regionId: placeRegion,
                }) === undefined
              }
              onClick={createPlace}
              className="rounded-md border border-neutral-300 px-3 py-1 text-xs disabled:opacity-40 dark:border-neutral-700"
            >
              {pinTarget === '' ? 'Add' : 'Pin'}
            </button>
          </div>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {/* The write path refuses a partial waypoint, and this says why
                before the refusal does. */}
            A pin needs all three, or leave all three blank for a place that is
            not on the map. Nobody sees a pin until its region is revealed.
          </p>
        </div>

        <div className="space-y-1">
          {/* **A table that predates the setup gate.** New campaigns cannot open
              without both halves, so this can only be an older one — and it
              warns rather than locks, because shutting an in-progress group out
              of their own game to enforce a rule added afterwards would be a
              cure worse than the fault. Named precisely, because "set up your
              map" is not actionable and "no scale, so no journey costs
              anything" is.
              **It does not promise that a map and a scale are enough.** They
              are two of three: a march also needs terrain, which lives in the
              region editor below and is what a brand-new table is missing. The
              warning said "until both exist", so it vanished the moment both
              did while travel still cost nothing — and the *march itself* is
              where the missing piece is now named, because that is the moment
              an Owner is asking the question. */}
          {(!worldMapUrl || scaleMiles === undefined) && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950 dark:text-amber-300">
              This table has{' '}
              {!worldMapUrl && scaleMiles === undefined
                ? 'no world map and no scale'
                : !worldMapUrl
                  ? 'no world map'
                  : 'no scale'}
              . Travel still narrates, but nothing is costed — no days pass, no getting-lost
              check is rolled and no encounter schedule is offered. The ground is a third
              piece: a march also needs a region with a terrain declared, or an imported
              world. Whichever piece is missing is named to you on the first march that
              needs it.
            </p>
          )}
          <label className="block text-xs text-neutral-500 dark:text-neutral-400" htmlFor="map-scale">
            How many miles across the map is
          </label>
          <div className="flex gap-2">
            <input
              id="map-scale"
              value={scale}
              onChange={(e) => setScale(e.target.value)}
              inputMode="numeric"
              placeholder="200"
              className="w-20 shrink-0 rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
            />
            <button
              type="button"
              onClick={saveScale}
              className="rounded-md border border-neutral-300 px-3 py-1 text-xs dark:border-neutral-700"
            >
              Set
            </button>
          </div>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {/* The one number that turns two pins into a distance. Without it
                travel declines to guess rather than inventing a day's march. */}
            Travel needs this to know how far anywhere is. Leave it blank to clear it — journeys
            then cost no time, rather than a made-up amount.
          </p>
        </div>

        <details className="rounded-md border border-neutral-200 p-2 dark:border-neutral-800">
          <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            Regions &amp; terrain
          </summary>
          <div className="mt-2">
            <RegionEditor campaignId={campaignId} onChanged={onPlacesChanged} />
          </div>
        </details>

        {/* Beneath the region editor rather than above it, deliberately: a
            declared region wins over an imported world, so the thing that
            overrides comes first. */}
        {/* ⚠ **Mounted only while open, not merely hidden by `details`.**
            Closed, this panel still occupied **2,620px** — Firefox keeps a
            closed `::details-content` laid out, so the box survives even though
            nothing paints. It escaped every container above it (the aside's own
            `scrollHeight` could not even see it) and stretched the *document*
            to 3,000px, which dragged the scroll position on every beat and made
            the table unusable. Bounding the ancestors does nothing, because as
            far as they are concerned the content is not there.
            Found by dumping the whole ancestor chain at once, after four rounds
            of guessing at CSS. See `docs/playtest-2026-08-25.md` #4/#12. */}
        <details
          className="rounded-md border border-neutral-200 p-2 dark:border-neutral-800"
          onToggle={(e) => setImportOpen(e.currentTarget.open)}
        >
          <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            Import a generated world
          </summary>
          {importOpen && (
            <div className="mt-2">
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                {/* ⚠ **Moved off the play surface.** Three file
                    inputs and an essay sat here, in the sidebar, beside the
                    transcript, during a session. Setting the map up is setup. */}
                Importing a generated world is part of{' '}
                <a href={`/campaigns/${campaignId}/map`} className="underline underline-offset-2">
                  setting up the map
                </a>
                .
              </p>
            </div>
          )}
        </details>

      {status && <p className="text-xs text-neutral-500 dark:text-neutral-400">{status}</p>}
    </div>
  );
}

/** Keys are `campaigns/<id>/<uuid>.png`; the tail is the only readable part. */
function shortKey(key: string): string {
  return key.split('/').at(-1) ?? key;
}
